import React, { useState, useEffect } from 'react';
import { profileAPI } from '../services/api';

const TwoFactorModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Setup, 2: Verify, 3: Success
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && step === 1) {
      generateSecret();
    }
  }, [isOpen, step]);

  const generateSecret = async () => {
    setIsLoading(true);
    try {
      const response = await profileAPI.generate2FASecret();
      setSecret(response.secret);
      setQrCode(response.qrCode);
      setStep(2);
    } catch (error) {
      setMessage(error.message || 'Failed to generate 2FA secret');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      await profileAPI.enable2FA({ secret, verificationCode });
      setStep(3);
      setMessage('2FA enabled successfully!');
    } catch (error) {
      setMessage(error.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSecret('');
    setQrCode('');
    setVerificationCode('');
    setMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        margin: '16px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
          }}>Enable Two-Factor Authentication</h2>
          <button
            onClick={handleClose}
            style={{
              color: '#6b7280',
              fontSize: '24px',
              fontWeight: 'bold',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {step === 1 && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: '24px', color: '#6b7280' }}>
              Two-Factor Authentication adds an extra layer of security to your account.
            </p>
            <button
              onClick={generateSecret}
              disabled={isLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: isLoading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}
            >
              {isLoading ? 'Generating...' : 'Start Setup'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px' }}>Step 1: Scan QR Code</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:
              </p>
              {qrCode && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <img 
                    src={qrCode} 
                    alt="QR Code for 2FA setup" 
                    style={{ border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
              )}
              <div style={{ 
                backgroundColor: '#f3f4f6', 
                padding: '12px', 
                borderRadius: '6px',
                fontSize: '12px',
                fontFamily: 'monospace',
                wordBreak: 'break-all'
              }}>
                <strong>Manual Entry:</strong> {secret}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px' }}>Step 2: Enter Verification Code</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                Enter the 6-digit code from your authenticator app:
              </p>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '18px',
                  textAlign: 'center',
                  letterSpacing: '2px',
                  outline: 'none'
                }}
                maxLength={6}
              />
            </div>

            {message && (
              <div style={{
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: message.includes('successfully') ? '#d1fae5' : '#fee2e2',
                color: message.includes('successfully') ? '#065f46' : '#991b1b',
                fontSize: '14px',
                marginBottom: '16px'
              }}>
                {message}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={verifyAndEnable2FA}
                disabled={isLoading || verificationCode.length !== 6}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (isLoading || verificationCode.length !== 6) ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (isLoading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer'
                }}
              >
                {isLoading ? 'Verifying...' : 'Enable 2FA'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', color: '#059669' }}>
              Two-Factor Authentication Enabled!
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Your account is now protected with 2FA. You'll need to enter a code from your authenticator app when logging in.
            </p>
            <button
              onClick={handleClose}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorModal;


